import { FC, useState, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import type {
  ScoringType,
  SignalLocationBand,
  TopicsFilter,
} from "@/lib/scoring/types";
import type { CachedTopic } from "@/types/intent";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import { GeoCountryCheckboxList } from "@/lib/company-filter/components/GeoCountryCheckboxList";
import { GeoRegionsSelector } from "@/lib/company-filter/components/GeoRegionsSelector";
import { DEFAULT_REGIONS } from "@/lib/company-filter/utils/geoSelection";
import { CountryInput } from "@/components/filters/Locations/CountryInput";
import { StateInput } from "@/components/filters/Locations/StateInput";
import type { RegionId } from "@/lib/company-filter/types";
import { pluralize } from "@/utils/text";
import { IntentTopicsSection } from "./IntentTopicsSection";

const DEFAULT_TOPICS: TopicsFilter = {
  ids: [],
  inclusionMethod: "ANY_PRESENT",
};

export const initialBand: SignalLocationBand = {
  location: { countryAlpha2s: [] },
  score: 0,
  topics: DEFAULT_TOPICS,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as SignalLocationBand[];
  if (typed.length === 0) return true;
  const topicIds = typed[0]?.topics?.ids ?? [];
  if (topicIds.length === 0) return true;
  return typed.some(
    (b) =>
      b.score <= 0 ||
      (!b.location.countryAlpha2s?.length &&
        !b.location.regionNames?.length &&
        !b.location.stateIds?.length)
  );
}

export function getBandSummary(band: unknown): string {
  const b = band as SignalLocationBand;
  const loc = b.location;
  if (loc.stateIds?.length)
    return pluralize("state/province", loc.stateIds.length);
  if (loc.regionNames?.length) return loc.regionNames.join(", ");
  if (loc.countryAlpha2s?.length)
    return `${loc.countryAlpha2s.length} ${pluralize("country", loc.countryAlpha2s.length)}`;
  return "—";
}

type GeoFilterType = "country" | "region" | "state";

function detectFilterType(bands: SignalLocationBand[]): GeoFilterType {
  if (bands.some((b) => b.location.stateIds?.length)) return "state";
  if (bands.some((b) => b.location.regionNames?.length)) return "region";
  return "country";
}

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const SignalLocationEditor: FC<Props> = ({ criterion, onChange }) => {
  const { countries } = useTaxonomyContext();
  const bands = criterion.location ?? [];

  const [topicsCache, setTopicsCache] = useState<CachedTopic[]>([]);
  const [topics, setTopics] = useState<TopicsFilter>(
    bands[0]?.topics ?? DEFAULT_TOPICS
  );

  const [filterType, setFilterType] = useState<GeoFilterType>(() =>
    detectFilterType(bands)
  );

  const [countrySearch, setCountrySearch] = useState<Record<number, string>>(
    {}
  );

  const countryOptions = useMemo(
    () =>
      countries
        .filter((c) => c.alpha2 && c.name)
        .map((c) => ({ code: c.alpha2!, name: c.name! })),
    [countries]
  );

  const stateCountry = bands[0]?.location.countryAlpha2s?.[0] ?? "US";

  const emptyBandForType = (
    type: GeoFilterType,
    topicsVal: TopicsFilter
  ): SignalLocationBand => {
    if (type === "state")
      return {
        location: { countryAlpha2s: [stateCountry], stateIds: [] },
        score: 0,
        topics: topicsVal,
      };
    if (type === "region")
      return { location: { regionNames: [] }, score: 0, topics: topicsVal };
    return { location: { countryAlpha2s: [] }, score: 0, topics: topicsVal };
  };

  const handleTopicsChange = useCallback(
    (newTopics: TopicsFilter, cache: CachedTopic[]) => {
      setTopics(newTopics);
      setTopicsCache(cache);
      onChange({
        ...criterion,
        location: bands.map((b) => ({ ...b, topics: newTopics })),
      });
    },
    [bands, criterion, onChange]
  );

  const handleFilterTypeChange = (
    _: unknown,
    newType: GeoFilterType | null
  ) => {
    if (!newType) return;
    setFilterType(newType);
    onChange({
      ...criterion,
      location: [emptyBandForType(newType, topics)],
    });
  };

  const handleStateCountryChange = (alpha2s: string[]) => {
    const alpha2 = alpha2s[0] ?? "US";
    const newBands = bands.map((b) => ({
      location: { countryAlpha2s: [alpha2], stateIds: [] },
      score: b.score,
      topics,
    }));
    onChange({ ...criterion, location: newBands });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      location: [
        ...bands.map((b) => ({ ...b, topics })),
        emptyBandForType(filterType, topics),
      ],
    });
  };

  const updateBandLocation = (
    index: number,
    locPatch: Partial<SignalLocationBand["location"]>
  ) => {
    onChange({
      ...criterion,
      location: bands.map((b, i) =>
        i === index
          ? { ...b, location: { ...b.location, ...locPatch }, topics }
          : { ...b, topics }
      ),
    });
  };

  const updateBandScore = (index: number, score: number) => {
    onChange({
      ...criterion,
      location: bands.map((b, i) =>
        i === index ? { ...b, score, topics } : { ...b, topics }
      ),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      location: bands
        .filter((_, i) => i !== index)
        .map((b) => ({ ...b, topics })),
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <IntentTopicsSection
        topics={topics}
        topicsCache={topicsCache}
        onChange={handleTopicsChange}
      />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={filterType}
        onChange={handleFilterTypeChange}
        sx={{
          mb: 1.5,
          "& .MuiToggleButton-root": { py: 0.5, px: 1.5, fontSize: 12 },
        }}
      >
        <ToggleButton value="region">Geopolitical</ToggleButton>
        <ToggleButton value="country">Country</ToggleButton>
        <ToggleButton value="state">State/Province</ToggleButton>
      </ToggleButtonGroup>

      {filterType === "state" && (
        <Box sx={{ mb: 1.5 }}>
          <CountryInput
            selectedItems={[stateCountry]}
            onCountrySelect={handleStateCountryChange}
            isMultiple={false}
            placeholderText="Select country…"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            Showing states/provinces for the selected country
          </Typography>
        </Box>
      )}

      {bands.map((band, i) => (
        <Box
          key={i}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
            mb: 1,
          }}
        >
          {filterType === "country" && (
            <GeoCountryCheckboxList
              title="Countries"
              countries={countryOptions}
              selectedCodes={band.location.countryAlpha2s ?? []}
              searchValue={countrySearch[i] ?? ""}
              onSearchChange={(v) =>
                setCountrySearch((prev) => ({ ...prev, [i]: v }))
              }
              onToggle={(code, checked) =>
                updateBandLocation(i, {
                  countryAlpha2s: checked
                    ? [...(band.location.countryAlpha2s ?? []), code]
                    : (band.location.countryAlpha2s ?? []).filter(
                        (c) => c !== code
                      ),
                })
              }
            />
          )}

          {filterType === "region" && (
            <GeoRegionsSelector
              regions={DEFAULT_REGIONS}
              selectedRegionIds={
                (band.location.regionNames ?? []).filter((r): r is RegionId =>
                  DEFAULT_REGIONS.some((dr) => dr.id === r)
                ) as RegionId[]
              }
              onToggle={(regionId, checked) =>
                updateBandLocation(i, {
                  regionNames: checked
                    ? [...(band.location.regionNames ?? []), regionId]
                    : (band.location.regionNames ?? []).filter(
                        (r) => r !== regionId
                      ),
                })
              }
            />
          )}

          {filterType === "state" && (
            <StateInput
              countryAlpha2={stateCountry}
              selectedItems={band.location.stateIds ?? []}
              onStateSelect={(_, stateIds) =>
                updateBandLocation(i, { stateIds })
              }
            />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Pts"
              error={
                band.score === 0 &&
                !!(
                  band.location.countryAlpha2s?.length ||
                  band.location.regionNames?.length ||
                  band.location.stateIds?.length
                )
              }
              value={band.score === 0 ? "" : band.score}
              onChange={(e) =>
                updateBandScore(
                  i,
                  e.target.value === "" ? 0 : Number(e.target.value)
                )
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 80 }}
            />
            <IconButton
              size="small"
              onClick={() => removeBand(i)}
              aria-label="Remove band"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
